import { useMutation } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { regenerateReportByPeriodEndpoint } from "@visioni/a3s-reports/client-messages";
import { isoDate } from "@visioni/a3s-codecs";
import { wasteSenseWeekStart } from "../wasteSenseWeekStart";

export interface RegenerateWasteSenseForWeekInput {
  /** An ISO-8601 instant inside the week to regenerate (the dump's timestamp). */
  readonly timestamp: string;
}

export interface UseRegenerateWasteSenseForWeekResult {
  /**
   * Fire-and-forget trigger. Resolves even on failure (including a 404 for a
   * week that has no report yet) — callers use it for background refresh and
   * must not surface an error to the user.
   */
  readonly regenerate: (input: RegenerateWasteSenseForWeekInput) => Promise<void>;
  readonly isPending: boolean;
}

/**
 * Fire-and-forget mutation that asks the backend to regenerate the WasteSense
 * report for the week containing a given instant (e.g. a just-confirmed dump),
 * so the report reflects the new dump without waiting for the next daily cron.
 *
 * It POSTs the existing by-period regenerate endpoint
 * (`…/reports/wastesense/period-start/{periodStart}/regenerate`, empty body),
 * which replays the report's stored `job.json` — the generator re-reads
 * confirmed dumps live over the report window, so a newly-written dump surfaces
 * on replay. Modelled on `useRegenerateRoadSenseLatest`.
 *
 * Best-effort by design: a **404** is expected and benign (no report has been
 * generated for that week yet — the next scheduled run will build it and pick
 * up the dump), and any other error is swallowed too. We never reject, so a
 * caller can `void regenerate(...)` right after a successful write without the
 * regen ever turning that write into a user-visible failure. The `periodStart`
 * is computed client-side (see {@link wasteSenseWeekStart}) to match the report
 * row's key exactly.
 */
export function useRegenerateWasteSenseForWeek(): UseRegenerateWasteSenseForWeekResult {
  const apiConfig = useApiConfig();

  const mutation = useMutation<void, Error, RegenerateWasteSenseForWeekInput>({
    // Never retry: a 404 is terminal (no row to replay) and this is
    // best-effort background work, so a transient failure just waits for the
    // next daily recompute rather than burning retries.
    retry: false,
    mutationFn: async ({ timestamp }) => {
      const { tenant, authenticatedFetch } = apiConfig;
      if (!tenant) {
        // Nothing to do without a resolved tenant. Return quietly rather than
        // throw — this is fire-and-forget.
        return;
      }

      // `periodStart` is the branded ISODate the endpoint's path schema expects;
      // `isoDate.parse` both brands and validates the YYYY-MM-DD shape.
      const periodStart = isoDate.parse(wasteSenseWeekStart(timestamp));
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(regenerateReportByPeriodEndpoint, {
          path: { tenantId: tenant.tenantId, type: "wastesense", periodStart },
        });

      try {
        // POST with an (intentionally empty) JSON body — the shared
        // `defineEndpoint` POST overload always registers a `bodySchema`, so
        // `{}` satisfies it. We deliberately ignore the response: this is
        // fire-and-forget, and a non-2xx (notably 404 for a not-yet-generated
        // week) is an expected no-op, not an error to surface.
        await authenticatedFetch(url, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({}),
        });
      } catch {
        // Swallow network/other errors: background refresh must never fail the
        // action that triggered it. The next daily cron self-heals the report.
      }
    },
  });

  return {
    regenerate: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}
